import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { DEFAULT_THEME, KEY_THEME, THEMES, type Theme } from './app.constants'
import type { AppState, CreateAppStoreOptions } from './app.types'

const applyThemeClasses = (theme: Theme, extraClasses: string[] = []) => {
  if (typeof window === 'undefined') return
  const root = document.documentElement
  root.classList.remove(...THEMES)
  root.classList.add(theme)
  if (extraClasses.length > 0) {
    root.classList.add(...extraClasses)
  }
}

const getInitialTheme = (extraClasses: string[] = []): Theme => {
  if (typeof window === 'undefined') return DEFAULT_THEME

  const savedTheme = localStorage.getItem(KEY_THEME)
  const isValidTheme = THEMES.includes(savedTheme as Theme)

  const theme = isValidTheme ? (savedTheme as Theme) : DEFAULT_THEME
  applyThemeClasses(theme, extraClasses)
  return theme
}

export const createAppStore = (options: CreateAppStoreOptions = {}) => {
  const { name = 'app-store', extraClasses = [], initialTheme } = options

  return create<AppState>()(
    devtools(
      (set, get) => ({
        theme: initialTheme || getInitialTheme(extraClasses),
        isLoading: false,
        isSidebarOpen: false,
        isSidebarCollapsed: false,

        setTheme: (theme) => {
          applyThemeClasses(theme, extraClasses)
          localStorage.setItem(KEY_THEME, theme)
          set({ theme })
        },

        toggleTheme: () => {
          const newTheme = get().theme === 'light' ? 'dark' : 'light'
          get().setTheme(newTheme)
        },

        setLoading: (loading) => set({ isLoading: loading }),

        setSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }),

        toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

        setSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),

        toggleSidebarCollapsed: () =>
          set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
      }),
      { name },
    ),
  )
}
